import PageHero from "@/components/landing/PageHero";
import { useSEO } from "@/hooks/useSEO";

export const PRIVACY_VERSION = "1.0.0";

const Privacy = () => {
  useSEO({
    title: "Privacy Policy | Pharmalify",
    description:
      "How Pharmalify collects, uses, stores and protects personal data and Customer Data across the Pharmalify Suite. GDPR-aligned, no AI training on customer data.",
    keywords:
      "Pharmalify privacy policy, GDPR pharma SaaS, biopharma data protection, no AI training, Customer Data privacy",
    canonical: "https://www.pharmalify.com/privacy",
  });

  return (
    <main className="min-h-screen bg-background">
      <PageHero
        title="Privacy Policy"
        description="How we collect, use, store and protect personal data and Customer Data across the Pharmalify Suite."
      />

      <section className="py-16 md:py-24">
        <div className="container max-w-3xl prose prose-slate dark:prose-invert mx-auto">
          <div className="not-prose mb-8 rounded-md border border-amber-500/30 bg-amber-500/5 px-4 py-3 text-xs text-amber-700 dark:text-amber-300">
            <strong>Draft v{PRIVACY_VERSION}</strong> — publishable working version pending final legal review. Specific
            controller entity, registered address, DPO contact and supervisory authority will be confirmed in a
            subsequent version.
          </div>

          <p className="text-sm text-muted-foreground mb-8">
            Version {PRIVACY_VERSION} — Effective Date: June 5, 2026
          </p>

          <h2>1. Scope</h2>
          <p>
            This Privacy Policy describes how Pharmalify ("we", "us", "Pharmalify") processes personal data when you
            visit our website (pharmalify.com), create an account, or use any module of the Pharmalify Suite (the
            "Platform"). It applies in addition to our Terms of Service and any Data Processing Addendum (DPA) signed
            with a Customer.
          </p>

          <h2>2. Data We Process</h2>
          <ul>
            <li><strong>Account data</strong>: name, work email, organization, role, password hash.</li>
            <li><strong>Authentication & security data</strong>: login timestamps, IP address, device/browser metadata, multi-factor verification events.</li>
            <li><strong>Customer Data</strong>: documents, records, investigations, contracts and other content you or your organization upload or generate on the Platform.</li>
            <li><strong>Usage & telemetry</strong>: page views, feature usage, error reports, performance metrics.</li>
            <li><strong>Communications</strong>: messages you send to support, sales or via in-app feedback.</li>
            <li><strong>Billing data</strong> (paid plans): handled by our payment processor (Stripe); we do not store full card numbers.</li>
          </ul>

          <h2>3. How We Use Data</h2>
          <ul>
            <li>To provide, secure, operate and improve the Platform.</li>
            <li>To authenticate users, prevent abuse and protect tenant isolation.</li>
            <li>To send transactional emails (account, billing, regulatory alerts you subscribed to).</li>
            <li>To comply with legal, audit and tax obligations.</li>
            <li>To analyze aggregated, de-identified usage to improve product quality.</li>
          </ul>
          <p>
            <strong>No AI training on Customer Data.</strong> Customer Data is never used to train, fine-tune or improve
            any foundation or third-party AI model. Dedicated, isolated fine-tuning is available only on explicit,
            opt-in written request and is scoped exclusively to the requesting Customer's organization.
          </p>

          <h2>4. Legal Bases (GDPR)</h2>
          <ul>
            <li><strong>Contract</strong> — to provide the Platform and fulfill the subscription agreement.</li>
            <li><strong>Legitimate interests</strong> — security, fraud prevention, product improvement, direct B2B communications.</li>
            <li><strong>Consent</strong> — non-essential cookies and marketing emails (opt-in, revocable at any time).</li>
            <li><strong>Legal obligation</strong> — tax, accounting, lawful requests from authorities.</li>
          </ul>

          <h2>5. Sub-processors</h2>
          <p>
            We rely on a limited set of vetted sub-processors to operate the Platform, including cloud infrastructure,
            authentication, email delivery, payment processing and AI inference providers. A current list of
            sub-processors and the regions in which they operate is available on request via{" "}
            <a href="mailto:privacy@pharmalify.com">privacy@pharmalify.com</a>.
          </p>

          <h2>6. International Transfers</h2>
          <p>
            Where personal data is transferred outside the EEA / UK, we rely on Standard Contractual Clauses or other
            lawful transfer mechanisms. EU-resident customers can request EU-region data residency where supported by
            the underlying infrastructure.
          </p>

          <h2>7. Security</h2>
          <p>
            We apply industry-standard controls: encryption at rest (AES-256), encryption in transit (TLS 1.2+),
            row-level security, role-based access control, tenant-level data isolation, immutable audit trails,
            least-privilege access for personnel and continuous vulnerability scanning. See our{" "}
            <a href="/security-compliance">Security &amp; Compliance</a> page for details.
          </p>

          <h2>8. Retention</h2>
          <p>
            Customer Data is retained for the duration of the subscription. On termination, Customer Data is deleted or
            returned per the executed agreement / DPA. Account, billing and audit-log records are retained as required
            by applicable law. Aggregated, de-identified usage telemetry may be retained indefinitely.
          </p>

          <h2>9. Your Rights</h2>
          <p>Subject to applicable law (GDPR, UK GDPR, CCPA and equivalents), you may have the right to:</p>
          <ul>
            <li>Access, rectify, port or delete your personal data;</li>
            <li>Restrict or object to certain processing;</li>
            <li>Withdraw consent at any time;</li>
            <li>Lodge a complaint with a supervisory authority.</li>
          </ul>
          <p>
            For Customer Data, requests should be directed to the Customer (data controller) who provisioned your
            account. For data we hold as controller, contact{" "}
            <a href="mailto:privacy@pharmalify.com">privacy@pharmalify.com</a>.
          </p>

          <h2>10. Cookies</h2>
          <p>
            We use a minimal set of cookies and similar technologies: <strong>strictly necessary</strong> (session,
            authentication, security) and, with your consent, <strong>analytics</strong> (Google Analytics 4 in
            anonymized mode). No cross-site advertising cookies are used. You can manage your choice at any time via
            the cookie banner.
          </p>

          <h2>11. Children</h2>
          <p>The Platform is a B2B tool and is not directed at individuals under 16.</p>

          <h2>12. Changes</h2>
          <p>
            We may update this Policy from time to time. Material changes will be communicated via the Platform or
            email and reflected in an updated version number above.
          </p>

          <h2>13. Contact</h2>
          <p>
            Privacy inquiries: <a href="mailto:privacy@pharmalify.com">privacy@pharmalify.com</a>
            <br />
            General contact: <a href="/contact">Contact Us</a>
          </p>
        </div>
      </section>
    </main>
  );
};

export default Privacy;
